import { cn } from '../../utils/cn';
import { Icon as IconCmp } from './Icon';

/**
 * Button — primary / secondary / danger / ghost varyantları.
 * Zuzzuu 2.0 app'lerindeki buton stilleri ile uyumlu.
 *
 * icon / iconRight: string (Icon name) veya React component kabul eder.
 */
function resolveIcon(icon) {
  if (!icon) return null;
  if (typeof icon === 'string') {
    return <IconCmp name={icon} className="h-4 w-4" />;
  }
  // React component
  const IconEl = icon;
  // Generic Icon component without a name prop won't render — guard
  if (IconEl === IconCmp) return null;
  return <IconEl className="h-4 w-4" />;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  // HTML varsayılanı "submit" — form içinde kazara submit'i tetiklemesin
  // diye "button"'a çekiyoruz. Gerçekten submit gereken yerlerde explicit
  // ``type="submit"`` prop'u geçilir.
  type = 'button',
  icon,
  iconRight,
  loading = false,
  disabled,
  className,
  children,
  ...rest
}) {
  // Shared Zuzzuu 2.0 buton stili: 10 px radius + yumuşak shadow ile hafif
  // 3D his; tüm variant'lar aynı base üzerinde oturuyor.
  const base =
    'inline-flex items-center justify-center gap-2 font-medium rounded-[10px] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]';

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  const variants = {
    primary:
      'bg-primary-600 text-white shadow-sm hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-500',
    secondary:
      'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50 focus:ring-neutral-300',
    danger: 'bg-danger-600 text-white shadow-sm hover:bg-danger-700 focus:ring-danger-500',
    success: 'bg-success-600 text-white shadow-sm hover:bg-success-700 focus:ring-success-500',
    warning: 'bg-warning-500 text-white shadow-sm hover:bg-warning-600 focus:ring-warning-500',
    ghost: 'bg-transparent text-neutral-600 hover:bg-neutral-100 focus:ring-neutral-300',
    outline:
      'bg-transparent border border-primary-600 text-primary-700 hover:bg-primary-50 focus:ring-primary-500',
  };

  return (
    <button
      type={type}
      className={cn(base, sizes[size], variants[variant], className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4zm2 5.3A8 8 0 014 12H0c0 3 1.1 5.8 3 7.9l3-2.6z"
          />
        </svg>
      )}
      {!loading && resolveIcon(icon)}
      {children}
      {!loading && resolveIcon(iconRight)}
    </button>
  );
}
